import React, { useState, useEffect } from 'react';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, PawPrint, Heart, MapPin, Trophy, Star,
  Instagram, ExternalLink, Sparkles, UserPlus, Search, MessageSquare, Check, X, Weight, Calendar
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function OwnerCommunity() {
  const { user } = useAuth();
  const [communityDogs, setCommunityDogs] = useState([]);
  const [communityFamilies, setCommunityFamilies] = useState([]);
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dogs');
  const [searchQuery, setSearchQuery] = useState('');
  const [breedFilter, setBreedFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [selectedDog, setSelectedDog] = useState(null);

  useEffect(() => {
    fetchCommunityData();
  }, []);

  const fetchCommunityData = async () => {
    try {
      const [dogsRes, familiesRes, statsRes, annRes] = await Promise.all([
        axios.get(`${API}/community/dogs`),
        axios.get(`${API}/community/families`),
        axios.get(`${API}/community/stats`),
        axios.get(`${API}/admin/announcements`).catch(() => ({ data: [] }))
      ]);
      setCommunityDogs(dogsRes.data);
      setCommunityFamilies(familiesRes.data);
      setStats(statsRes.data);
      setAnnouncements(annRes.data || []);

      // Fetch connection statuses for each family owner
      if (user) {
        const statusMap = {};
        for (const fam of familiesRes.data) {
          if (fam.owner_id !== user.id) {
            try {
              const statusRes = await axios.get(`${API}/connections/status/${user.id}/${fam.owner_id}`);
              statusMap[fam.owner_id] = statusRes.data;
            } catch (e) { /* ignore */ }
          }
        }
        setConnectionStatuses(statusMap);
      }
    } catch (error) {
      console.error('Error fetching community data:', error);
    } finally {
      setLoading(false);
    }
  };

  const personalityEmojis = {
    playful: '🎾',
    energetic: '⚡',
    calm: '😌',
    shy: '🙈',
  };

  const handleConnect = async (ownerId) => {
    if (!user) return;
    try {
      await axios.post(`${API}/connections/request`, {
        requester_id: user.id,
        target_id: ownerId
      });
      setConnectionStatuses(prev => ({...prev, [ownerId]: {status: 'pending', requester_id: user.id}}));
      toast.success('Connection request sent!');
    } catch (error) {
      toast.error('Failed to send request');
    }
  };

  // Filtering
  const allBreeds = [...new Set(communityDogs.map(d => d.breed).filter(Boolean))];
  const allLocations = [...new Set(communityDogs.map(d => [d.location_city, d.location_state].filter(Boolean).join(', ')).filter(Boolean))];

  const filteredDogs = communityDogs.filter(dog => {
    if (searchQuery && !dog.name.toLowerCase().includes(searchQuery.toLowerCase()) && !dog.breed.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (breedFilter && dog.breed !== breedFilter) return false;
    if (locationFilter) {
      const loc = [dog.location_city, dog.location_state].filter(Boolean).join(', ');
      if (loc !== locationFilter) return false;
    }
    return true;
  });

  const filteredFamilies = communityFamilies.filter(fam => {
    if (searchQuery && !fam.owner_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <Heart className="w-12 h-12 text-edupaws-pink animate-bounce" />
            <span className="text-edupaws-navy font-body">Loading community...</span>
          </div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      {/* Hero Section */}
      <div className="glass-card p-8 rounded-3xl mb-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-edupaws-sage/10 via-transparent to-edupaws-pink/10"></div>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 glass-pink px-4 py-2 rounded-full mb-4">
            <Heart className="w-5 h-5 text-edupaws-pink" />
            <span className="font-body font-medium text-edupaws-navy">Welcome to our pack!</span>
            <Sparkles className="w-5 h-5 text-edupaws-sage" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-edupaws-navy mb-3">
            Edu Paws Community
          </h1>
          <p className="text-gray-500 font-body max-w-2xl mx-auto">
            Meet the amazing dogs and families in our training community. Share your journey, 
            connect with fellow pet parents, and celebrate every milestone together!
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-4 rounded-xl text-center">
            <div className="w-12 h-12 mx-auto mb-2 glass-sage rounded-full flex items-center justify-center">
              <PawPrint className="w-6 h-6 text-edupaws-navy" />
            </div>
            <div className="font-heading text-2xl font-bold text-edupaws-navy">{stats.total_dogs}</div>
            <div className="text-xs text-gray-500 font-body">Dogs Training</div>
          </div>
          <div className="glass-card p-4 rounded-xl text-center">
            <div className="w-12 h-12 mx-auto mb-2 glass-pink rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-edupaws-navy" />
            </div>
            <div className="font-heading text-2xl font-bold text-edupaws-navy">{stats.total_families}</div>
            <div className="text-xs text-gray-500 font-body">Families</div>
          </div>
          <div className="glass-card p-4 rounded-xl text-center">
            <div className="w-12 h-12 mx-auto mb-2 bg-edupaws-navy/10 rounded-full flex items-center justify-center">
              <Trophy className="w-6 h-6 text-edupaws-navy" />
            </div>
            <div className="font-heading text-2xl font-bold text-edupaws-navy">{stats.total_videos_completed}</div>
            <div className="text-xs text-gray-500 font-body">Videos Completed</div>
          </div>
          <div className="glass-card p-4 rounded-xl text-center">
            <div className="w-12 h-12 mx-auto mb-2 bg-amber-100 rounded-full flex items-center justify-center">
              <Star className="w-6 h-6 text-amber-500" />
            </div>
            <div className="font-heading text-2xl font-bold text-edupaws-navy">
              {stats.top_breeds?.[0]?.breed || 'N/A'}
            </div>
            <div className="text-xs text-gray-500 font-body">Top Breed</div>
          </div>
        </div>
      )}

      {/* Community Announcements */}
      {announcements.length > 0 && (
        <div className="glass-card p-5 rounded-2xl mb-6" data-testid="community-announcements">
          <h3 className="font-heading font-bold text-edupaws-navy mb-3 flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-edupaws-sage" />
            From the Edu Paws Team
          </h3>
          <div className="space-y-3">
            {announcements.slice(0, 3).map((ann) => (
              <div key={ann.id} className="glass-sage p-3 rounded-xl">
                <div className="font-heading font-bold text-edupaws-navy text-sm">{ann.title}</div>
                <p className="text-gray-600 font-body text-sm mt-0.5">{ann.message}</p>
                <div className="text-xs text-gray-400 font-body mt-1">
                  {new Date(ann.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('dogs')}
          data-testid="tab-dogs"
          className={`px-6 py-3 rounded-xl font-heading font-bold transition-all
            ${activeTab === 'dogs' 
              ? 'bg-edupaws-navy text-white shadow-[0_4px_0_#3C4753]' 
              : 'glass-card text-edupaws-navy hover:bg-white/80'}`}
        >
          <PawPrint className="w-4 h-4 inline mr-2" />
          Dogs ({communityDogs.length})
        </button>
        <button
          onClick={() => setActiveTab('families')}
          data-testid="tab-families"
          className={`px-6 py-3 rounded-xl font-heading font-bold transition-all
            ${activeTab === 'families' 
              ? 'bg-edupaws-navy text-white shadow-[0_4px_0_#3C4753]' 
              : 'glass-card text-edupaws-navy hover:bg-white/80'}`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Families ({communityFamilies.length})
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={activeTab === 'dogs' ? 'Search dogs by name or breed...' : 'Search families...'}
            data-testid="community-search"
            className="h-10 pl-9 rounded-xl"
          />
        </div>
        {activeTab === 'dogs' && (
          <>
            <select
              value={breedFilter}
              onChange={(e) => setBreedFilter(e.target.value)}
              data-testid="breed-filter"
              className="h-10 px-3 rounded-xl border border-gray-200 font-body text-sm bg-white text-gray-700 focus:ring-2 focus:ring-edupaws-sage"
            >
              <option value="">All Breeds</option>
              {allBreeds.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              data-testid="location-filter"
              className="h-10 px-3 rounded-xl border border-gray-200 font-body text-sm bg-white text-gray-700 focus:ring-2 focus:ring-edupaws-sage"
            >
              <option value="">All Locations</option>
              {allLocations.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </>
        )}
      </div>

      {/* Dogs Grid */}
      {activeTab === 'dogs' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDogs.length === 0 ? (
            <div className="col-span-full text-center py-12 glass-card rounded-2xl">
              <PawPrint className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-body">No dogs match your filters</p>
            </div>
          ) : (
          filteredDogs.map((dog) => (
            <div key={dog.id} className="community-card" data-testid={`community-dog-${dog.id}`}>
              {/* Dog Photo */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={dog.photo_url || 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400'}
                  alt={dog.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Location Badge */}
                {dog.location_city && (
                  <div className="absolute top-3 left-3 glass px-3 py-1 rounded-full flex items-center gap-1 text-xs">
                    <MapPin className="w-3 h-3 text-edupaws-navy" />
                    <span className="text-edupaws-navy font-body">{dog.location_city}, {dog.location_state}</span>
                  </div>
                )}

                {/* Personality Badge */}
                {dog.personality && (
                  <div className="absolute top-3 right-3 glass px-3 py-1 rounded-full text-xs font-body">
                    {personalityEmojis[dog.personality]} {dog.personality}
                  </div>
                )}

                {/* Name Overlay */}
                <div className="absolute bottom-3 left-3 text-white">
                  <h3 className="font-heading text-xl font-bold">{dog.name}</h3>
                  <p className="text-white/80 text-sm font-body">{dog.breed} • {dog.age} yrs</p>
                </div>
              </div>

              {/* Dog Info */}
              <div className="p-4">
                {/* Owner */}
                <div className="flex items-center gap-2 mb-3 text-sm text-gray-500 font-body">
                  <Heart className="w-4 h-4 text-edupaws-pink" />
                  Family: {dog.owner_name}
                </div>

                {/* Fun Fact */}
                {dog.fun_facts && (
                  <p className="text-sm text-gray-600 font-body line-clamp-2 mb-4 italic">
                    "{dog.fun_facts.slice(0, 100)}..."
                  </p>
                )}

                {/* Social Links */}
                <div className="flex items-center gap-3">
                  {dog.tiktok_url && (
                    <a
                      href={dog.tiktok_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-icon social-icon-tiktok"
                      title="TikTok"
                    >
                      <TikTokIcon className="w-4 h-4" />
                    </a>
                  )}
                  {dog.instagram_url && (
                    <a
                      href={dog.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-icon social-icon-instagram"
                      title="Instagram"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {!dog.tiktok_url && !dog.instagram_url && (
                    <span className="text-xs text-gray-400 font-body">No social links</span>
                  )}
                  
                  <div className="ml-auto">
                    <button
                      onClick={() => setSelectedDog(dog)}
                      data-testid={`view-profile-${dog.id}`}
                      className="text-xs text-edupaws-sage font-body font-medium flex items-center gap-1 hover:text-edupaws-navy transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" /> View Profile
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
          )}
        </div>
      )}

      {/* Families Grid */}
      {activeTab === 'families' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFamilies.length === 0 ? (
            <div className="col-span-full text-center py-12 glass-card rounded-2xl">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-body">No families match your search</p>
            </div>
          ) : (
          filteredFamilies.map((family) => (
            <div key={family.owner_id} className="glass-card p-6 rounded-2xl" data-testid={`community-family-${family.owner_id}`}>
              {/* Family Header */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 glass-sage rounded-full flex items-center justify-center">
                  <span className="font-heading text-xl font-bold text-edupaws-navy">
                    {family.owner_name.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-edupaws-navy">
                    The {family.owner_name.split(' ').pop()} Family
                  </h3>
                  <p className="text-sm text-gray-500 font-body">
                    {family.member_count} members • {family.dog_count} dog{family.dog_count !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Dogs */}
              <div className="mb-4">
                <h4 className="text-sm font-body font-medium text-gray-600 mb-2">Our Pups:</h4>
                <div className="flex flex-wrap gap-2">
                  {family.dogs.map((dog) => (
                    <div key={dog.id} className="flex items-center gap-2 glass px-3 py-1.5 rounded-full">
                      {dog.photo_url ? (
                        <img src={dog.photo_url} alt={dog.name} className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <PawPrint className="w-4 h-4 text-edupaws-sage" />
                      )}
                      <span className="text-sm font-body text-edupaws-navy">{dog.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Family Members */}
              {family.family_members.length > 0 && (
                <div>
                  <h4 className="text-sm font-body font-medium text-gray-600 mb-2">Family Members:</h4>
                  <div className="flex -space-x-2">
                    {family.family_members.slice(0, 5).map((member) => (
                      <img
                        key={member.id}
                        src={member.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.name}`}
                        alt={member.name}
                        title={member.name}
                        className="w-8 h-8 rounded-full border-2 border-white bg-white"
                      />
                    ))}
                    {family.family_members.length > 5 && (
                      <div className="w-8 h-8 rounded-full bg-edupaws-sage/20 border-2 border-white flex items-center justify-center">
                        <span className="text-xs font-body text-edupaws-navy">+{family.family_members.length - 5}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Connect / Message Button */}
              {user && family.owner_id !== user.id && (
                <div className="mt-4 pt-3 border-t border-white/30">
                  {connectionStatuses[family.owner_id]?.status === 'accepted' ? (
                    <a href={`/owner/messages`}
                      className="w-full flex items-center justify-center gap-2 bg-edupaws-navy text-white py-2 rounded-xl font-body text-sm font-medium hover:bg-navy-700 transition-colors">
                      <MessageSquare className="w-4 h-4" /> Message
                    </a>
                  ) : connectionStatuses[family.owner_id]?.status === 'pending' ? (
                    <div className="w-full flex items-center justify-center gap-2 glass-sage text-edupaws-navy py-2 rounded-xl font-body text-sm font-medium">
                      <Check className="w-4 h-4" /> Request Sent
                    </div>
                  ) : (
                    <button
                      onClick={() => handleConnect(family.owner_id)}
                      data-testid={`connect-${family.owner_id}`}
                      className="w-full flex items-center justify-center gap-2 bg-edupaws-sage text-white py-2 rounded-xl font-body text-sm font-medium hover:bg-sage-600 transition-colors">
                      <UserPlus className="w-4 h-4" /> Connect
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
          )}
        </div>
      )}

      {/* Empty State */}
      {communityDogs.length === 0 && communityFamilies.length === 0 && (
        <div className="glass-card p-12 rounded-2xl text-center">
          <div className="w-20 h-20 mx-auto mb-6 glass-pink rounded-full flex items-center justify-center">
            <Heart className="w-10 h-10 text-edupaws-pink" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-edupaws-navy mb-4">
            Be the First to Join!
          </h2>
          <p className="text-gray-500 font-body max-w-md mx-auto">
            Our community is just getting started. Add your dog and make their profile public to appear here!
          </p>
        </div>
      )}

      {/* Dog Profile Dialog */}
      <Dialog open={!!selectedDog} onOpenChange={() => setSelectedDog(null)}>
        <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl" data-testid="dog-profile-dialog">
          {selectedDog && (
            <>
              {/* Hero Photo */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={selectedDog.photo_url || 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600'}
                  alt={selectedDog.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-5 text-white">
                  <h2 className="font-heading text-2xl font-bold">{selectedDog.name}</h2>
                  <p className="text-white/90 font-body text-sm">{selectedDog.breed}</p>
                </div>
                {selectedDog.personality && (
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-body font-medium text-edupaws-navy">
                    {personalityEmojis[selectedDog.personality]} {selectedDog.personality}
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="p-5 space-y-4">
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <Calendar className="w-4 h-4 mx-auto text-edupaws-sage mb-1" />
                    <div className="font-heading font-bold text-edupaws-navy text-sm">{selectedDog.age} yrs</div>
                    <div className="text-xs text-gray-400 font-body">Age</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <Weight className="w-4 h-4 mx-auto text-edupaws-sage mb-1" />
                    <div className="font-heading font-bold text-edupaws-navy text-sm">{selectedDog.weight || '-'} lbs</div>
                    <div className="text-xs text-gray-400 font-body">Weight</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <MapPin className="w-4 h-4 mx-auto text-edupaws-sage mb-1" />
                    <div className="font-heading font-bold text-edupaws-navy text-sm truncate">
                      {selectedDog.location_city || '-'}
                    </div>
                    <div className="text-xs text-gray-400 font-body">Location</div>
                  </div>
                </div>

                {/* Owner */}
                <div className="flex items-center gap-2 text-sm text-gray-600 font-body">
                  <Heart className="w-4 h-4 text-edupaws-sage" />
                  Family: <span className="font-medium text-edupaws-navy">{selectedDog.owner_name}</span>
                </div>

                {/* Fun Facts */}
                {selectedDog.fun_facts && (
                  <div className="bg-edupaws-sage/5 rounded-xl p-4">
                    <p className="text-sm text-gray-700 font-body italic">"{selectedDog.fun_facts}"</p>
                  </div>
                )}

                {/* Social Links */}
                <div className="flex items-center gap-3 pt-2">
                  {selectedDog.tiktok_url && (
                    <a href={selectedDog.tiktok_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-full text-sm font-body transition-colors">
                      <TikTokIcon className="w-4 h-4" /> TikTok
                    </a>
                  )}
                  {selectedDog.instagram_url && (
                    <a href={selectedDog.instagram_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 bg-pink-50 hover:bg-pink-100 px-4 py-2 rounded-full text-sm font-body text-pink-700 transition-colors">
                      <Instagram className="w-4 h-4" /> Instagram
                    </a>
                  )}
                  {!selectedDog.tiktok_url && !selectedDog.instagram_url && (
                    <span className="text-sm text-gray-400 font-body">No social links yet</span>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </OwnerLayout>
  );
}
